import {
    black,
    white,
    red400,
    red500,
    red600,
    blue50,
    blue100,
    blue400,
    blue500,
    blue600,
    green100,
    green400,
    green500,
    neutral50,
    neutral60,
    neutral100,
    neutral200,
    neutral300,
    neutral400,
    neutral600,
    neutral700,
    neutral800,
    Colors,
    orange300,
    red100,
    red200,
    neutral80,
    gold100,
} from '@prezi/common-design';

import { darkenHex, lightenHex } from '../utils';

export type ColorKeys = keyof Colors;
export type ModifiedColor = string;
export type Color = Colors[ColorKeys];

enum FontWeightOptions {
    thin = 100,
    extralight = 200,
    light = 300,
    regular = 400,
    medium = 500,
    semibold = 600,
    bold = 700,
    extrabold = 800,
    black = 900,
}

type FontWeight = {
    thin?: FontWeightOptions.thin;
    extralight?: FontWeightOptions.extralight;
    light?: FontWeightOptions.light;
    regular?: FontWeightOptions.regular;
    medium?: FontWeightOptions.medium;
    semibold?: FontWeightOptions.semibold;
    bold?: FontWeightOptions.bold;
    extrabold?: FontWeightOptions.extrabold;
    black?: FontWeightOptions.black;
};

type FontDefinition = {
    family: string;
    weight: FontWeight;
};

export type ColorDefinition = {
    main: Color;
    pale?: Color;
    weak: Color;
    medium?: Color;
    strong: Color;
    contrast?: Color;
};

export type OverlayDefinition = {
    main: (color: Color) => Color;
};

type BentoLookAndFeel = {
    radius: number;
    transition: number;
    overscrollBehavior: 'auto' | 'contain' | 'none';
};

type BentoPalette = {
    mode: 'light' | 'dark';
    colors: {
        transparent: Color;
        text: ColorDefinition;
        background: ColorDefinition;
        border: ColorDefinition;
        primary: ColorDefinition;
        secondary: ColorDefinition;
        danger: ColorDefinition;
        success: ColorDefinition;
        neutral: ColorDefinition;
        overlay?: OverlayDefinition;
    };
};

type BentoTypography = {
    font: {
        primary: FontDefinition;
    };
};

export type BentoTheme = BentoPalette & BentoTypography & BentoLookAndFeel;

const bentoBaseLookAndFeel: BentoLookAndFeel = {
    radius: 4,
    transition: 0.2,
    overscrollBehavior: 'auto',
};

const bentoBaseTypography: BentoTypography = {
    font: {
        primary: {
            family: 'Raleway, Helvetica, sans-serif',
            weight: {
                regular: FontWeightOptions.regular,
                medium: FontWeightOptions.medium,
                semibold: FontWeightOptions.semibold,
                bold: FontWeightOptions.bold,
                extrabold: FontWeightOptions.extrabold,
            },
        },
    },
};

const bentoLight: BentoTheme = {
    ...bentoBaseLookAndFeel,
    ...bentoBaseTypography,
    mode: 'light',
    colors: {
        transparent: 'rgba(255,255,255,0)',
        text: {
            main: neutral600,
            weak: neutral400,
            pale: neutral300,
            strong: black,
            contrast: white,
        },
        background: {
            main: white,
            weak: neutral50,
            medium: neutral80,
            strong: neutral100,
        },
        border: {
            main: neutral100,
            medium: neutral200,
            weak: neutral60,
            strong: neutral300,
        },
        primary: {
            main: blue500,
            medium: blue400,
            pale: blue50,
            weak: blue100,
            strong: blue600,
        },
        secondary: {
            main: gold100,
            weak: darkenHex(gold100, 6),
            strong: orange300,
        },
        danger: {
            main: red500,
            medium: red400,
            weak: red200,
            pale: red100,
            strong: red600,
        },
        success: {
            main: green400,
            weak: green100,
            strong: green500,
        },
        neutral: {
            main: neutral300,
            pale: neutral60,
            weak: neutral200,
            strong: neutral600,
        },
        overlay: {
            main: (color: Color) => darkenHex(color, 8),
        },
    },
};

const bentoDark: BentoTheme = {
    ...bentoBaseLookAndFeel,
    ...bentoBaseTypography,
    mode: 'dark',
    colors: {
        transparent: 'rgba(0,0,0,0)',
        text: {
            main: white,
            weak: neutral200,
            strong: neutral50,
            contrast: neutral800,
        },
        background: {
            main: neutral600,
            weak: neutral700,
            strong: neutral800,
        },
        border: {
            main: neutral300,
            weak: neutral200,
            strong: neutral400,
        },
        primary: {
            main: blue500,
            medium: blue400,
            pale: blue50,
            weak: blue600,
            strong: blue100,
        },
        secondary: {
            main: gold100,
            weak: lightenHex(gold100, 4),
            strong: darkenHex(gold100, 6),
        },
        danger: {
            main: red500,
            weak: red400,
            strong: red600,
        },
        success: {
            main: green400,
            weak: green500,
            strong: green100,
        },
        neutral: {
            main: neutral300,
            pale: neutral600,
            weak: neutral400,
            strong: neutral600,
        },
        overlay: {
            main: (color: Color) => lightenHex(color, 18),
        },
    },
};

export const bento = {
    light: bentoLight,
    dark: bentoDark,
};
